import { isNotEmpty } from '@vy/array';
import { isPerUnitVariant, hasMeterReset } from '@vy/types';
import { isNullOrUndefined } from '@vy/utils';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useChargeContext } from '../../charge-context';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';

export function useAutoRefill() {
  const { itemIndex: productIndex } = useChargeContext();
  const { watch, setValue } = useFormContext<PlanInputForm>();

  const commitment = watch(`usageProducts.${productIndex}.commitment`);
  const accumulationInterval = watch(`usageProducts.${productIndex}.accumulationInterval`);
  const variants = watch(`usageProducts.${productIndex}.variants`);

  const isPerUnit =
    isNotEmpty(variants) &&
    variants.every((variant: { pricing: { pricingModel: string } }) => isPerUnitVariant(variant));
  const isMeterResetEnabled = hasMeterReset(accumulationInterval);
  const isAutoRefillForced = isPerUnit && isMeterResetEnabled;

  useEffect(() => {
    if (!isAutoRefillForced) {
      return;
    }

    if (isNullOrUndefined(commitment)) {
      return;
    }

    setValue(`usageProducts.${productIndex}.commitment.autoRefillEnabled`, true);
    setValue(`usageProducts.${productIndex}.commitment.replenishmentInterval`, accumulationInterval);
  }, [isAutoRefillForced, commitment, accumulationInterval, productIndex, setValue]);

  const autoRefillTooltipContent = isAutoRefillForced
    ? 'Auto refill is required for per-unit products with meter reset'
    : isNullOrUndefined(accumulationInterval)
      ? 'Enable meter reset to set auto refill'
      : '';

  const isAutoRefillDisabledByMissingMeterReset = isNullOrUndefined(accumulationInterval);
  const isAutoRefillDisabled = isAutoRefillDisabledByMissingMeterReset || isAutoRefillForced;
  const isAutoRefillChecked = (value: boolean | undefined): boolean => (isAutoRefillForced ? true : !!value);

  return {
    autoRefillTooltipContent,
    isAutoRefillDisabled,
    isAutoRefillChecked,
    accumulationInterval,
  };
}
